import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CreditCard, MessageCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const brl = (n) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(n || 0));

export default function PublicPay() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/public/pay/${token}/info`)
      .then(r => setInfo(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Link inválido"));
  }, [token]);

  const start = async (mode) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/pay/${token}/checkout`, { payment_mode: mode });
      if (r.data?.checkout_url) window.location.href = r.data.checkout_url;
      else throw new Error();
    } catch (e) { toast.error("Erro ao gerar checkout"); }
    finally { setBusy(false); }
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="p-8 max-w-md text-center">
        <h1 className="font-display text-xl">Link inválido</h1>
        <p className="text-sm text-neutral-500 mt-2">{err}</p>
      </Card>
    </div>
  );
  if (!info) return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Loader2 size={32} className="animate-spin"/>
    </div>
  );
  const daysLeft = info.days_left;
  const isExpired = daysLeft !== null && daysLeft < 0;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-neutral-50 to-white dark:from-[#0A0F19] dark:to-[#050810]">
      <Card className="p-7 max-w-md w-full shadow-xl border-[#E5E5E5]" data-testid="public-pay-card">
        <div className="text-center">
          <h1 className="font-display text-2xl">{isExpired ? "Seu plano expirou" : "Renove seu plano"}</h1>
          <p className="text-xs text-neutral-500 mt-2">
            {isExpired ? "Para continuar usando o Gestão Expert, escolha uma forma de pagamento abaixo." :
              `${daysLeft || 0} dias restantes. Renove com antecedência para não perder acesso.`}
          </p>
        </div>
        <div className="mt-5 rounded-lg border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Plano</div>
          <div className="font-medium text-lg">{info.plan_label}</div>
          <div className="mt-2 flex items-baseline justify-between">
            <div>
              {info.discount && (
                <span className="text-xs line-through text-neutral-400 mr-2">{brl(info.plan_price)}</span>
              )}
              <span className="font-display text-2xl">{brl(info.final_price)}</span>
              <span className="text-xs text-neutral-500 ml-1">/mês</span>
            </div>
            {info.discount && (
              <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">
                {info.discount.kind === "percent" ? `${info.discount.value}% off` : `-${brl(info.discount.value)}`}
              </span>
            )}
          </div>
        </div>

        <div className="mt-4 grid gap-2">
          <Button onClick={()=>start("card")} disabled={busy} className="w-full bg-[#635BFF] hover:bg-[#5749f0] text-white" data-testid="public-pay-card">
            <CreditCard size={14} className="mr-2"/> Assinar com cartão (recorrente)
          </Button>
          <Button onClick={()=>start("pix")} disabled={busy} variant="outline" className="w-full" data-testid="public-pay-pix">
            <CreditCard size={14} className="mr-2"/> Pagar 1 mês (PIX ou cartão avulso)
          </Button>
        </div>
        <p className="text-[10px] text-center text-neutral-500 flex items-center justify-center gap-1 mt-4">
          <ShieldCheck size={10}/> Pagamento processado com segurança pelo Stripe
        </p>
      </Card>
    </div>
  );
}
